import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AdminElasticsearchBentoBrand } from "schemas/admin";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

interface RelatedProfile {
  username: string;
  profilePicUrl: string | null;
  isPrivate: boolean;
}

interface SeedResult {
  bentoBrand: AdminElasticsearchBentoBrand | null;
  relatedProfiles: RelatedProfile[];
  esScore: number | null;
}

const formatFollowers = (count: number | null): string => {
  if (count == null) return "—";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
};

const FACTOR_LABELS: Partial<
  Record<keyof AdminElasticsearchBentoBrand, string>
> = {
  nicheness: "Nicheness",
  countryCode: "Country",
  contactedAmount: "Contacted",
  instagramPostingFrequency: "IG Posting Freq",
  instagramMostRecentPost: "IG Recent Post",
  instagramFollowerCount: "IG Followers",
  instagramModernessScore: "IG Modernness",
  instagramPostCount: "IG Post Count",
  instagramRelatedProfilesProcessed: "Profiles Processed",
  isSocialMediaVerified: "SM Verified",
  wasRequested: "Was Requested",
  tier: "Tier",
  tiktokUsername: "TikTok",
  tiktokPostingFrequency: "TT Posting Freq",
  tiktokMostRecentPost: "TT Recent Post",
  tiktokHeartCount: "TT Hearts",
};

const formatFactorValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
};

const EsFactorsPanel = ({
  factors,
}: {
  factors: AdminElasticsearchBentoBrand;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "2px 16px",
      mt: 1,
    }}
  >
    {(Object.keys(FACTOR_LABELS) as (keyof AdminElasticsearchBentoBrand)[]).map(
      (key) => {
        const value = factors[key];
        const isNull = value === null || value === undefined;
        return (
          <Box
            key={key}
            sx={{ display: "flex", gap: 0.5, alignItems: "baseline" }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {FACTOR_LABELS[key]}:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isNull ? "text.disabled" : "text.primary",
                fontWeight: isNull ? 400 : 500,
              }}
            >
              {formatFactorValue(value)}
            </Typography>
          </Box>
        );
      },
    )}
  </Box>
);

const SeedingPreview = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [configJson, setConfigJson] = useState<string>("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [results, setResults] = useState<SeedResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [expandedFactors, setExpandedFactors] = useState<Set<number>>(
    new Set(),
  );

  useEffect(() => {
    fetchDefaultConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDefaultConfig = async () => {
    setConfigLoading(true);
    try {
      const data = await fetcherAuth(
        getToken,
        "/api/admin/potential-bento-brands/instagram-seeding-config",
        "GET",
      );
      setConfigJson(JSON.stringify(data, null, 2));
      setJsonError(null);
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setConfigLoading(false);
    }
  };

  const handleConfigChange = (value: string) => {
    setConfigJson(value);
    if (!value.trim()) {
      setJsonError(null);
      return;
    }
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  const handleFormat = () => {
    try {
      setConfigJson(JSON.stringify(JSON.parse(configJson), null, 2));
      setJsonError(null);
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  const handlePreview = async () => {
    let config;
    try {
      config = JSON.parse(configJson);
    } catch {
      setAlert("Invalid JSON", "warning");
      return;
    }

    setLoading(true);
    try {
      const { results: previewResults } = await fetcherAuth(
        getToken,
        "/api/admin/potential-bento-brands/preview-instagram-seeding",
        "POST",
        {},
        config,
      );
      setResults(previewResults);
      setExpandedFactors(new Set());
      if (previewResults.length === 0) {
        setAlert("No seed candidates found for this config", "warning");
      }
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  const toggleFactors = (id: number) => {
    setExpandedFactors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <StyledPage
      content={
        <Grid sx={{ mt: 2 }}>
          <Typography variant="h3" gutterBottom>
            <strong>Instagram Scraping Preview</strong>
          </Typography>
          <Box
            sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}
          >
            <LoadingButton
              variant="outlined"
              loading={configLoading}
              onClick={fetchDefaultConfig}
            >
              Load Current Query
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              onClick={() =>
                setConfigJson(JSON.stringify({ query: {}, size: 10 }, null, 2))
              }
            >
              Clear Query
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              onClick={handleFormat}
              disabled={!configJson.trim()}
            >
              Format JSON
            </LoadingButton>
          </Box>

          <TextField
            multiline
            fullWidth
            minRows={10}
            maxRows={30}
            value={configJson}
            onChange={(e) => handleConfigChange(e.target.value)}
            placeholder='{"query": {"function_score": {...}}, "size": 10}'
            error={!!jsonError}
            helperText={jsonError ?? undefined}
            inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
            sx={{ mb: 2 }}
          />

          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handlePreview}
            disabled={!configJson.trim() || !!jsonError}
          >
            Preview
          </LoadingButton>

          {results !== null && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" gutterBottom>
                {results.length} Seed Candidate{results.length !== 1 ? "s" : ""}
              </Typography>
              <Grid container spacing={2}>
                {results.map((result, idx) => (
                  <Grid item xs={12} md={6} key={result.bentoBrand?.id ?? idx}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                sx={{ minWidth: 20 }}
                              >
                                #{idx + 1}
                              </Typography>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {result.bentoBrand?.brandName ?? "(unnamed)"}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="a"
                              href={`https://instagram.com/${result.bentoBrand?.instagramUsername}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                ml: "28px",
                                display: "block",
                              }}
                            >
                              @{result.bentoBrand?.instagramUsername}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                            }}
                          >
                            {result.esScore != null && (
                              <Chip
                                label={`score ${result.esScore.toFixed(2)}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                            {result.bentoBrand?.countryCode && (
                              <Chip
                                label={result.bentoBrand.countryCode}
                                size="small"
                              />
                            )}
                            <Chip
                              label={`${formatFollowers(result.bentoBrand?.instagramFollowerCount ?? null)} followers`}
                              size="small"
                              variant="outlined"
                            />
                            {result.bentoBrand?.nicheness != null && (
                              <Chip
                                label={`nicheness ${result.bentoBrand.nicheness}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                          onClick={() =>
                            result.bentoBrand &&
                            toggleFactors(result.bentoBrand.id)
                          }
                        >
                          <Typography variant="caption" color="text.secondary">
                            {result.bentoBrand &&
                            expandedFactors.has(result.bentoBrand.id)
                              ? "▾"
                              : "▸"}{" "}
                            Scoring factors
                          </Typography>
                        </Box>
                        <Collapse
                          in={
                            !!result.bentoBrand &&
                            expandedFactors.has(result.bentoBrand.id)
                          }
                        >
                          <EsFactorsPanel factors={result.bentoBrand!} />
                        </Collapse>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 1, mb: 0.5 }}
                        >
                          {result.relatedProfiles.length} related profile
                          {result.relatedProfiles.length !== 1 ? "s" : ""}
                          {result.relatedProfiles.length === 0
                            ? " (no related profiles)"
                            : ""}
                        </Typography>

                        {result.relatedProfiles.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {result.relatedProfiles.map((profile) => (
                              <Chip
                                key={profile.username}
                                label={
                                  profile.isPrivate
                                    ? `@${profile.username} 🔒`
                                    : `@${profile.username}`
                                }
                                size="small"
                                component="a"
                                href={`https://instagram.com/${profile.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                clickable
                                variant="outlined"
                                sx={{ fontSize: 11 }}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </Grid>
      }
    />
  );
};

export default SeedingPreview;
